// Hideo - the saving and online state of this device (Pinia), read by the Vue dialogs and the save indicator and changed
// only by the saving code (src/features/storage/, src/features/cloud/):
// - SYNC: per profile, changes not saved yet and the last file saved or loaded (localStorage creatorOS_sync);
// - DRIVE: per online service, connection, account, folders and the file linked to each profile (localStorage creatorOS_drive);
// - AS: the online autosave at this moment (src/features/cloud/autosync.ts);
// - the autosave pause message of every service, and whether a manual "Save online" / "Load online" is running.
// Formats and keys are the ones of the old legacy code (docs/data.md): never change them. The state is read from this
// browser once, when the store is first used (loadApp, src/features/boot.ts, while the app loads: the same moment as before).
import { defineStore } from 'pinia'
import { reactive, ref, shallowReactive, toRaw } from 'vue'
import type { SyncState } from '../features/cloud'
import { SYNC_KEY } from '../data/profiles'

/** State of an online service on this device (DRIVE.p[id]) */
export interface CloudState {
  connected?: boolean
  email?: string
  /** profile id → its file on the service */
  links: Record<string, string>
  /** profile id → version of the file last seen here */
  seen: Record<string, string>
  /** Dropbox sign-in (access token, its expiry, refresh token) */
  auth?: { access?: string; exp?: number; refresh?: string }
  /** Google: the "Hideo" folder and its subfolders (Immagini, Vocali) */
  folderId?: string
  sub?: Record<string, string>
}
/** Every service's state on this device (localStorage creatorOS_drive) */
interface DriveState { p: Record<string, Partial<CloudState>> }

/** Online autosave state. state: '' | 'ok' | 'error' | 'offline' | 'auth' (a tap is needed to reactivate Google);
 *  at = time of the last successful save */
interface AutoSyncState {
  timer: ReturnType<typeof setTimeout> | null
  first: number
  running: boolean
  again: boolean
  retry: number
  retryTimer: ReturnType<typeof setTimeout> | null
  state: '' | 'ok' | 'error' | 'offline' | 'auth'
  at: number
  lastCheck: number
  checking: boolean
  loading: boolean
  conflict: boolean
  lastRenew: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const readJson = (key: string): any => { try { return JSON.parse(localStorage.getItem(key) as string) || {} } catch { return {} } }

export const useSyncStore = defineStore('sync', () => {
  /** Save state of every profile, by id (creatorOS_sync). The values are replaced, never changed in place */
  const SYNC = shallowReactive<Record<string, SyncState>>(readJson(SYNC_KEY))

  let drive = readJson('creatorOS_drive')
  if (!drive.p) {   // older state (Google Drive only): becomes the Google state
    const old = drive; drive = { p: {} }
    if (old.connected) drive.p.google = { connected: true, email: old.email || '', folderId: old.folderId || '', sub: old.sub || {}, links: old.links || {}, seen: old.seen || {} }
  }
  /** Every service's state on this device (creatorOS_drive) */
  const DRIVE = reactive<DriveState>(drive)

  const AS = shallowReactive<AutoSyncState>({ timer: null, first: 0, running: false, again: false, retry: 0, retryTimer: null, state: '', at: 0, lastCheck: 0, checking: false, loading: false, conflict: false, lastRenew: 0 })
  /** Autosave pause message of each service ('' = none) */
  const autoPause = reactive<Record<string, string>>({})
  /** Every service's pause message together ('' = none): the dot on the profile button and the Profiles window */
  const paused = ref('')
  /** Bumped by setSync: what shows the save state (Profiles window, service choice, Home card) reads it, so a save
   *  redraws only them, not every page */
  const ver = ref(0)
  /** A manual "Save online" / "Load online" is running: the autosave waits */
  const busy = ref(false)

  /** A profile's save state ({} when it has none) */
  const syncOf = (id: string): SyncState => SYNC[id] || {}
  /** A service's state, created when missing (links and seen always exist). It is created on the plain object, so a
   *  component that reads it while drawing does not change what it depends on */
  function CS(id: string): CloudState {
    const p = toRaw(DRIVE.p) as Record<string, Partial<CloudState>>
    const s = p[id] || (p[id] = {})
    s.links = s.links || {}; s.seen = s.seen || {}
    return DRIVE.p[id] as CloudState
  }
  /** The service is connected on this device */
  const cloudOn = (id: string): boolean => !!(DRIVE.p[id] && DRIVE.p[id].connected)

  return { SYNC, DRIVE, AS, autoPause, paused, ver, busy, syncOf, CS, cloudOn }
})
